import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ProductShell, Button, Logo } from '@cloudgenie/ui';
import { API_BASE, useAuth } from '../AuthContext';

const GOOGLE_LOGIN_PATH = '/api/auth/google-native/login';

export default function SignupPage() {
  const [params] = useSearchParams();
  const next = params.get('next') || '';
  const { refresh } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(fmt(data?.detail) || 'Signup failed. Please try again.');
        return;
      }
      await refresh();
      if (next && /^https?:\/\//.test(next)) window.location.href = next;
      else window.location.href = '/';
    } finally {
      setBusy(false);
    }
  }

  function goGoogle() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use ?next= (backend stashes + allow-list validates) — NOT ?state=,
    // which Authlib reserves for CSRF protection.
    const q = next ? `?next=${encodeURIComponent(next)}` : '';
    window.location.href = `${API_BASE}${GOOGLE_LOGIN_PATH}${q}`;
  }

  return (
    <ProductShell product="id" title="" subtitle="">
      <div className="max-w-md mx-auto mt-4 mb-16" data-testid="signup-card">
        <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md shadow-2xl shadow-primary/5 p-8">
          <div className="flex flex-col items-center text-center mb-8">
            <Logo product="id" size={44} />
            <h1 className="mt-4 text-2xl font-semibold tracking-tight">Create your account</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Start with a free CloudGenie workspace.
            </p>
          </div>

          {/* Google first — same treatment as LoginPage */}
          <button
            type="button"
            onClick={goGoogle}
            data-testid="signup-google"
            className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-background hover:bg-card transition px-4 py-2.5 text-sm font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.73v2.27h2.9c1.7-1.57 2.69-3.87 2.69-6.64z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.27c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34C2.44 15.98 5.48 18 9 18z" fill="#34A853"/>
              <path d="M3.95 10.71c-.18-.54-.28-1.12-.28-1.71 0-.59.1-1.17.28-1.71V4.96H.96C.35 6.17 0 7.55 0 9c0 1.45.35 2.83.96 4.04l2.99-2.33z" fill="#FBBC05"/>
              <path d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.88 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.99 2.34C4.66 5.17 6.65 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Sign up with Google
          </button>

          <div className="relative py-5">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/60" /></div>
            <span className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground bg-card/60 px-3">or use your work email</span>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground" htmlFor="name">Full name</label>
              <input id="name" data-testid="signup-name" type="text" required minLength={1} maxLength={100}
                value={name} onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground" htmlFor="email">Work email</label>
              <input id="email" data-testid="signup-email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground" htmlFor="password">Password <span className="text-muted-foreground">(8+ characters)</span></label>
              <input id="password" data-testid="signup-password" type="password" required minLength={8} maxLength={128}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
            </div>
            {error && (
              <div data-testid="signup-error" className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-400">
                {error}
              </div>
            )}
            <Button variant="primary" type="submit" disabled={busy || !email || !password || !name}
              data-testid="signup-submit" className="w-full">
              {busy ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ''}`} className="text-primary hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-muted-foreground">
          By creating an account you agree to our{' '}
          <a href="https://www.cloudgenie.co/legal/terms" className="hover:text-foreground underline underline-offset-4">Terms</a>
          {' '}and{' '}
          <a href="https://www.cloudgenie.co/legal/privacy" className="hover:text-foreground underline underline-offset-4">Privacy Policy</a>.
        </p>
      </div>
    </ProductShell>
  );
}

function fmt(detail: unknown): string {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof (detail as any).msg === 'string') return (detail as any).msg;
  return String(detail);
}
